import { useEffect, useCallback, useRef, useState } from 'react';

import { expVal, expValEquals } from '@confluence/feature-experiments';
import { useIsAIEnabledForContent } from '@confluence/ai-common/entry-points/useIsAIEnabledForContent.ts';

export interface UsePasteDetectionProps {
	contentId: string;
	userOffline?: boolean;
	isEditorEmpty?: boolean;
}

export interface UsePasteDetectionReturn {
	documentContext: string;
	pasteDetected: boolean;
}

export const usePasteDetection = ({
	contentId,
	userOffline,
	isEditorEmpty,
}: UsePasteDetectionProps): UsePasteDetectionReturn => {
	const { isAIEnabledForContent } = useIsAIEnabledForContent({ contentId });
	const proseMirrorElementRef = useRef<HTMLElement | null>(null);
	const [documentContext, setDocumentContext] = useState<string>('');
	const [pasteDetected, setPasteDetected] = useState(false);

	const isFeatureEnabled =
		isAIEnabledForContent &&
		!userOffline &&
		expValEquals('confluence_ai_title_generation_on_paste', 'isEnabled', true);

	const findProseMirrorElement = useCallback((): HTMLElement | null => {
		const filteredProseMirrors = Array.from(document.querySelectorAll('.ProseMirror')).filter(
			(el) => !el.closest('[data-testid="chromeless-editor"]'),
		);
		return filteredProseMirrors[0] as HTMLElement | null;
	}, []);

	useEffect(() => {
		if (!isFeatureEnabled) {
			return;
		}

		const proseMirrorElement = findProseMirrorElement();
		proseMirrorElementRef.current = proseMirrorElement;

		if (!proseMirrorElement) {
			return;
		}

		const handlePaste = (event: ClipboardEvent) => {
			const pastedText = event.clipboardData?.getData('text/plain') || '';

			if (
				isEditorEmpty &&
				pastedText.length > expVal('confluence_ai_title_generation_on_paste', 'charLength', 50)
			) {
				setTimeout(() => {
					const extractedContext = proseMirrorElement.textContent || '';
					if (extractedContext.trim().length > 0) {
						setDocumentContext(extractedContext);
						setPasteDetected(true);
					}
				}, 100);
			}
		};

		proseMirrorElement.addEventListener('paste', handlePaste);

		return () => {
			proseMirrorElement.removeEventListener('paste', handlePaste);
		};
	}, [findProseMirrorElement, isFeatureEnabled, isEditorEmpty]);

	useEffect(() => {
		if (pasteDetected) {
			const timer = setTimeout(() => setPasteDetected(false), 500);
			return () => clearTimeout(timer);
		}
	}, [pasteDetected]);

	return {
		documentContext,
		pasteDetected,
	};
};
